/**
 * Thin fetch wrapper for the hub API. All requests go through `/api/*` which
 * Vite proxies to the hub in dev (see vite.config.ts). In production the hub
 * and the web app sit behind the same reverse proxy so the same relative URLs
 * resolve directly.
 */

const BASE = "/api/v1";

class ApiError extends Error {
  constructor(status, body) {
    super(`HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function request(path, init = {}) {
  const res = await fetch(BASE + path, {
    ...init,
    // Always send the session cookie so dashboard endpoints stay authenticated
    // across page reloads. In dev (Vite proxy → hub) both are same-origin
    // anyway; in prod they likely will be too, but explicit doesn't hurt.
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...(init.headers || {}) },
  });
  // 502 for "test notification" comes back with a JSON body containing the
  // delivery error — we want to surface that to the UI rather than throw a
  // generic ApiError that hides the message.
  if (!res.ok) {
    let body;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => "");
    }
    const err = new ApiError(res.status, body);
    err.body = body;
    throw err;
  }
  return res.json();
}

export const api = {
  listServers: () => request("/dashboard/servers"),
  createServer: (body) =>
    request("/dashboard/servers", { method: "POST", body: JSON.stringify(body) }),
  deleteServer: (id) =>
    request(`/dashboard/servers/${encodeURIComponent(id)}`, { method: "DELETE" }),
  updateServer: (id, patch) =>
    request(`/dashboard/servers/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  serverMetrics: (id, { since, limit, range } = {}) => {
    const params = new URLSearchParams();
    if (range != null) params.set("range", String(range));
    if (since != null) params.set("since", String(since));
    if (limit != null) params.set("limit", String(limit));
    const qs = params.toString();
    return request(`/dashboard/servers/${encodeURIComponent(id)}/metrics${qs ? "?" + qs : ""}`);
  },
  getSettings: () => request("/dashboard/settings"),
  updateSettings: (patch) =>
    request("/dashboard/settings", { method: "PUT", body: JSON.stringify(patch) }),
  testNotification: () =>
    request("/dashboard/settings/test-notification", { method: "POST" }),
  listNotifications: (limit = 200) => request(`/dashboard/notifications?limit=${limit}`),
  serverProcesses: (id) =>
    request(`/dashboard/servers/${encodeURIComponent(id)}/processes`),
  sendCommand: (id, cmd) =>
    request(`/dashboard/servers/${encodeURIComponent(id)}/command`, {
      method: "POST",
      body: JSON.stringify(cmd),
    }),
  wsStatus: (id) =>
    request(`/dashboard/servers/${encodeURIComponent(id)}/ws-status`),
  geoLookup: (host) =>
    request(`/dashboard/geo/${encodeURIComponent(host)}`),

  // ── auth ──
  me: () => request("/auth/me"),
  login: (password) => request("/auth/login", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => request("/auth/logout", { method: "POST" }),

  // ── sessions ──
  listSessions: () => request("/auth/sessions"),
  revokeSession: (fullId) =>
    request(`/auth/sessions/${encodeURIComponent(fullId)}/revoke`, { method: "POST" }),
  revokeAllSessions: () =>
    request("/auth/sessions/revoke-all", { method: "POST" }),

  // ── password ──
  changePassword: (currentPassword, newPassword) =>
    request("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }),

  // ── system info / backup / restore ──
  systemInfo: () => request("/dashboard/system/info"),

  // Fetch the backup JSON and trigger a browser download. We bypass the
  // `request()` helper because we need raw text/blob handling + a download
  // filename pulled from Content-Disposition.
  downloadBackup: async () => {
    const res = await fetch(BASE + "/dashboard/backup", { credentials: "same-origin" });
    if (!res.ok) {
      let body;
      try { body = await res.json(); } catch { body = await res.text().catch(() => ""); }
      const err = new ApiError(res.status, body);
      throw err;
    }
    const disposition = res.headers.get("content-disposition") || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = match?.[1] ?? `monibot-backup-${new Date().toISOString().slice(0, 10)}.json`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { filename, size: blob.size };
  },

  // Restore expects the parsed JSON body (caller already FileReader-ed it).
  restoreBackup: (backupJson) =>
    request("/dashboard/restore", {
      method: "POST",
      body: JSON.stringify(backupJson),
    }),

  // ── maintenance: wipe transactional data (server master data + config
  // preserved). Each destructive call requires `confirm: "HAPUS"` in body.
  maintenanceStats: () => request("/dashboard/maintenance/stats"),
  clearNotifications: (confirm) =>
    request("/dashboard/maintenance/clear-notifications", {
      method: "POST",
      body: JSON.stringify({ confirm }),
    }),
  clearMetricsHistory: (confirm) =>
    request("/dashboard/maintenance/clear-metrics", {
      method: "POST",
      body: JSON.stringify({ confirm }),
    }),
};

export { ApiError };
